import Big from "big.js";

// Catálogo de tarifas de IVA del SRI. Espejo de backend.models.TaxCatalog:
// la CLAVE es el codigoPorcentaje que viaja al XML, el valor es la tarifa en %.
const IVA_RATES = {
    '0': '0',
    '5': '5',
    '8': '8',
    '2': '12',  // histórico
    '3': '14',  // histórico
    '4': '15',
};

// Tarifas que se ofrecen al usuario: solo las vigentes.
const IVA_OPTIONS = [
    { label: '0%',  value: '0' },
    { label: '5%',  value: '5' },
    { label: '15%', value: '4' },
];

function rateForCode(code) {
    return new Big(IVA_RATES[code] !== undefined ? IVA_RATES[code] : '0');
}

// Etiqueta legible de una tarifa. Cubre códigos históricos que ya no se ofrecen
// pero pueden venir en datos viejos.
function labelForCode(code) {
    return IVA_RATES[code] !== undefined ? IVA_RATES[code] + '%' : '0%';
}

// Puente para ítems/productos legacy que solo traen el binario `hasTax`: la
// tarifa correcta es la general vigente del negocio, no una asumida. Solo se
// usa cuando el payload no trae ivaCode (el backend siempre lo envía).
function codeFromHasTax(hasTax, businessTaxPercentage) {
    if (!hasTax) { return '0'; }
    const found = Object.keys(IVA_RATES).find(function (code) {
        return IVA_RATES[code] === String(businessTaxPercentage);
    });
    return found || '4';
}

/**
 * Agrega IVA e ICE por tarifa. Espejo exacto de backend.models.computeTaxTotals:
 * se acumula por tarifa a precisión completa, se redondea CADA bucket a 2
 * decimales y recién de ahí se derivan sub0/sub12/tax. Así el invariante
 * sub0 + sub12 + tax == total se cumple por construcción y el total en pantalla
 * coincide al centavo con lo que persiste el backend.
 *
 * `lines`: array de { ivaCode, base } con base como Big a precisión completa.
 * Devuelve { buckets, sub0, sub12, tax } — sub12 es TODA la base gravada
 * (5% + 15% + ...), no solo la de la tarifa general.
 */
function computeTaxTotals(lines) {
    const rawIva = {};
    const rawIce = {};

    lines.forEach(function (line) {
        const base = line.base;
        const iceValue = line.iceValue || new Big('0.0');

        if (!iceValue.eq(0)) {
            const iceKey = line.iceCode || '0';
            if (rawIce[iceKey] === undefined) {
                rawIce[iceKey] = { base: new Big('0.0'), valor: new Big('0.0') };
            }
            rawIce[iceKey].base = rawIce[iceKey].base.plus(base);
            rawIce[iceKey].valor = rawIce[iceKey].valor.plus(iceValue);
        }

        const code = line.ivaCode || '0';
        const rate = rateForCode(code);
        if (rawIva[code] === undefined) {
            rawIva[code] = { rate: rate, base: new Big('0.0'), cleanBase: new Big('0.0'), valor: new Big('0.0') };
        }
        const ivaBase = base.plus(iceValue);   // <- la cascada
        rawIva[code].base = rawIva[code].base.plus(ivaBase);
        rawIva[code].cleanBase = rawIva[code].cleanBase.plus(base);
        rawIva[code].valor = rawIva[code].valor.plus(ivaBase.times(rate).div(100));
    });

    let sub0 = new Big('0.0');
    let sub12 = new Big('0.0');
    let tax = new Big('0.0');
    let iceTotal = new Big('0.0');
    const buckets = [];

    Object.keys(rawIva).forEach(function (code) {
        const bucket = rawIva[code];
        const base = bucket.base.round(2);
        const clean = bucket.cleanBase.round(2);
        const valor = bucket.valor.round(2);
        buckets.push({ codigo: '2', ivaCode: code, rate: bucket.rate, base: base, cleanBase: clean, valor: valor });
        if (bucket.rate.gt(0)) { sub12 = sub12.plus(clean); } else { sub0 = sub0.plus(clean); }
        tax = tax.plus(valor);
    });

    Object.keys(rawIce).forEach(function (code) {
        const bucket = rawIce[code];
        const valor = bucket.valor.round(2);
        buckets.push({ codigo: '3', iceCode: code, rate: new Big('0'), base: bucket.base.round(2), valor: valor });
        iceTotal = iceTotal.plus(valor);
    });

    return { buckets: buckets, sub0: sub0, sub12: sub12, tax: tax, iceTotal: iceTotal };
}

/**
 * Base gravada a una tarifa concreta. Acepta indistintamente los buckets que
 * devuelve computeTaxTotals ({rate, base}) o las taxLines serializadas del
 * backend ({tarifa, baseImponible}), para no duplicar el parseo en cada modelo.
 */
function baseAtRate(buckets, rate) {
    const target = new Big(rate);
    return (buckets || []).reduce(function (acc, b) {
        if (b.codigo === '3') { return acc; }   // ICE no es una tarifa de IVA
        const r = (b.rate !== undefined) ? new Big(b.rate) : new Big(b.tarifa || '0');
        if (!r.eq(target)) { return acc; }
        // Convención del SRI: el "subtotal" es el valor ANTES de impuestos, así
        // que se prefiere la base limpia; baseImponible incluye el ICE.
        if (b.cleanBase !== undefined) { return acc.plus(b.cleanBase); }
        // `baseSinIce` se agregó DESPUÉS de que ya existieran líneas: las viejas
        // quedaron en 0 por el default de la columna. Un 0 legítimo solo ocurre
        // si baseImponible también es 0 (el ICE se deriva de la base, nunca la
        // agota), así que esta condición identifica una línea previa.
        const sinIce = new Big(b.baseSinIce || '0');
        const imponible = new Big(b.baseImponible !== undefined ? b.baseImponible : (b.base || '0'));
        return acc.plus(sinIce.eq(0) && !imponible.eq(0) ? imponible : sinIce);
    }, new Big('0.0'));
}

export { IVA_RATES, IVA_OPTIONS, rateForCode, labelForCode, codeFromHasTax, computeTaxTotals, baseAtRate };
